import { useEffect, useState } from "react";
import { http, API } from "../api";
import { Vote, Users, Car, FileText, Search, Plus } from "lucide-react";
import { Modal, PageHeader, AddButton, FormRow, EmptyState } from "../shared";

export default function ElectionDay() {
  const [tab, setTab] = useState("live");
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [nonVoted, setNonVoted] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [boothFilter, setBoothFilter] = useState("");
  const [q, setQ] = useState("");
  const [showVeh, setShowVeh] = useState(false);
  const [vehForm, setVehForm] = useState({ registration_no: "", driver_name: "", driver_mobile: "", vehicle_type: "Car", capacity: 4, booth: "42", status: "Available" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, c, v] = await Promise.all([
      http.get("/turnout/summary"),
      http.get("/contacts"),
      http.get("/vehicles"),
    ]);
    setSummary(s.data); setContacts(c.data); setVehicles(v.data);
    const nv = await http.get(`/turnout/non-voted${boothFilter ? `?booth=${boothFilter}` : ""}`);
    setNonVoted(nv.data);
  };
  useEffect(() => { load(); }, [boothFilter]);

  const toggleVote = async (c) => {
    const voted = !(c._voted || false);
    await http.post("/turnout/mark", { contact_id: c.contact_id, voted });
    load();
  };

  const votedIds = new Set(); // We derive from summary counts by refetch
  const filtered = contacts.filter((c) => (!boothFilter || c.booth === boothFilter) && (!q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.epic || "").toLowerCase().includes(q.toLowerCase())));

  // We know a contact voted if its id is NOT in nonVoted list (when same booth filter is applied)
  const nonVotedIds = new Set(nonVoted.map((n) => n.contact_id));

  const printSlip = (contact_id) => window.open(`${API}/voter-slip/${contact_id}`, "_blank");
  const printBoothSlips = (booth) => window.open(`${API}/voter-slip/booth/${booth}/pdf`, "_blank");

  const saveVehicle = async () => {
    setSaving(true);
    try { await http.post("/vehicles", { ...vehForm, capacity: Number(vehForm.capacity) }); setShowVeh(false); load(); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader eyebrow="ELECTION DAY · LIVE" title="Election companion"
        subtitle="Turnout · voter slips · non-voted list · vehicle roster"
        actions={<>
          {boothFilter && <button className="outline" data-testid="print-booth-slips-button" onClick={() => printBoothSlips(boothFilter)}><FileText size={15} /> Print booth {boothFilter} slips</button>}
        </>} />

      {summary && (
        <div className="stats" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          <div className="stat" data-testid="stat-turnout">
            <div className="stat-icon teal"><Vote size={19} /></div>
            <span>Overall turnout</span><strong>{summary.pct}%</strong>
            <small className="trend">{summary.voted} of {summary.total}</small>
          </div>
          <div className="stat" data-testid="stat-non-voted">
            <div className="stat-icon amber"><Users size={19} /></div>
            <span>Yet to vote</span><strong>{summary.total - summary.voted}</strong>
            <small className="trend">across all booths</small>
          </div>
          <div className="stat" data-testid="stat-booths">
            <div className="stat-icon blue"><Vote size={19} /></div>
            <span>Booths tracked</span><strong>{summary.booths.length}</strong>
            <small className="trend">Ward 7</small>
          </div>
          <div className="stat" data-testid="stat-vehicles">
            <div className="stat-icon red"><Car size={19} /></div>
            <span>Vehicles ready</span><strong>{vehicles.filter((v) => v.status === "Available").length}</strong>
            <small className="trend">{vehicles.length} total</small>
          </div>
        </div>
      )}

      <div className="chip-row">
        {["live", "booths", "non-voted", "vehicles"].map((t) => (
          <button key={t} className={`chip-filter ${tab === t ? "active" : ""}`} data-testid={`election-tab-${t}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === "live" && (
        <section className="panel">
          <div className="directory-toolbar">
            <div className="inner-search"><Search size={17} /><input data-testid="turnout-search" placeholder="Search voter by name / EPIC" value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <div className="filters">
              <select data-testid="booth-filter" value={boothFilter} onChange={(e) => setBoothFilter(e.target.value)} style={{ padding: "8px 11px", fontSize: 10 }}>
                <option value="">All booths</option>
                {summary?.booths.map((b) => <option key={b.booth} value={b.booth}>Booth {b.booth}</option>)}
              </select>
            </div>
          </div>
          <div className="people-table">
            <div className="tr th"><span>Voter</span><span>Booth · EPIC</span><span>Status</span><span>Actions</span></div>
            {filtered.slice(0, 60).map((c, i) => {
              const voted = !nonVotedIds.has(c.contact_id) && nonVoted.length > 0 && !!boothFilter && boothFilter === c.booth;
              return (
                <div key={c.contact_id} className="tr" data-testid={`turnout-row-${i}`}>
                  <span className="person">
                    <span className="avatar person-avatar">{c.name.split(" ").map((x) => x[0]).join("")}</span>
                    <span><b>{c.name}</b><small>{c.gender} · {c.age || "—"}</small></span>
                  </span>
                  <span>Booth {c.booth}<br /><small>{c.epic || "—"}</small></span>
                  <span>
                    <button className={`chip ${voted ? "green" : "amber"}`} data-testid={`toggle-vote-${i}`} onClick={() => toggleVote({ ...c, _voted: voted })} style={{ cursor: "pointer", border: 0 }}>
                      {voted ? "✓ Voted" : "Not yet"}
                    </button>
                  </span>
                  <span><button className="link" data-testid={`print-slip-${i}`} onClick={() => printSlip(c.contact_id)}><FileText size={12} /> Slip</button></span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {tab === "booths" && summary && (
        <section className="panel">
          <div className="finance-table">
            <div className="tr th"><span>Booth</span><span>Total voters</span><span>Voted</span><span>Turnout %</span><span></span></div>
            {summary.booths.map((b, i) => (
              <div key={b.booth} className="tr" data-testid={`booth-row-${i}`}>
                <span><b>Booth {b.booth}</b></span>
                <span>{b.total}</span>
                <span><b>{b.voted}</b></span>
                <span>
                  <div className="progress-bar" style={{ width: 120, display: "inline-block", marginRight: 8, verticalAlign: "middle" }}>
                    <div className="progress-fill" style={{ width: `${b.pct}%` }} />
                  </div>
                  {b.pct}%
                </span>
                <span><button className="link" data-testid={`booth-print-${i}`} onClick={() => printBoothSlips(b.booth)}>Print slips</button></span>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "non-voted" && (
        <section className="panel">
          {nonVoted.length === 0 ? <EmptyState icon={Vote} title="Nobody left" hint="Everyone in the current filter has voted." /> : (
            <div className="people-table">
              <div className="tr th"><span>Voter</span><span>Booth / EPIC</span><span>Mobile</span><span></span></div>
              {nonVoted.slice(0, 100).map((c, i) => (
                <div key={c.contact_id} className="tr" data-testid={`non-voted-row-${i}`}>
                  <span className="person">
                    <span className="avatar person-avatar">{c.name.split(" ").map((x) => x[0]).join("")}</span>
                    <span><b>{c.name}</b><small>{c.gender} · {c.age || "—"}</small></span>
                  </span>
                  <span>Booth {c.booth}<br /><small>{c.epic || "—"}</small></span>
                  <span>{c.mobile || "—"}</span>
                  <span><button className="link" data-testid={`nv-slip-${i}`} onClick={() => printSlip(c.contact_id)}>Print slip</button></span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "vehicles" && (
        <section className="panel">
          <div className="directory-toolbar">
            <div style={{ flex: 1 }}></div>
            <AddButton onClick={() => setShowVeh(true)} label="Add vehicle" testid="vehicle-add-button" />
          </div>
          {vehicles.length === 0 ? <EmptyState icon={Car} title="No vehicles" hint="Register a vehicle for voter transport." /> : (
            <div className="finance-table">
              <div className="tr th"><span>Vehicle</span><span>Driver</span><span>Booth</span><span>Capacity</span><span>Status</span></div>
              {vehicles.map((v, i) => (
                <div key={v.vehicle_id} className="tr" data-testid={`vehicle-row-${i}`}>
                  <span><b>{v.registration_no}</b><small> {v.vehicle_type}</small></span>
                  <span><b>{v.driver_name}</b><small> {v.driver_mobile}</small></span>
                  <span>Booth {v.booth}</span>
                  <span>{v.capacity} seats</span>
                  <span><span className={`chip ${v.status === "Available" ? "green" : v.status === "Busy" ? "amber" : "red"}`}>{v.status}</span></span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {showVeh && (
        <Modal title="Register vehicle" onClose={() => setShowVeh(false)} testid="vehicle-modal">
          <div className="form-grid">
            <FormRow label="Registration no"><input data-testid="form-reg-no" value={vehForm.registration_no} onChange={(e) => setVehForm({ ...vehForm, registration_no: e.target.value })} /></FormRow>
            <FormRow label="Vehicle type"><select data-testid="form-vtype" value={vehForm.vehicle_type} onChange={(e) => setVehForm({ ...vehForm, vehicle_type: e.target.value })}><option>Car</option><option>Auto</option><option>Van</option><option>Bike</option></select></FormRow>
            <FormRow label="Driver name"><input data-testid="form-driver-name" value={vehForm.driver_name} onChange={(e) => setVehForm({ ...vehForm, driver_name: e.target.value })} /></FormRow>
            <FormRow label="Driver mobile"><input data-testid="form-driver-mobile" value={vehForm.driver_mobile} onChange={(e) => setVehForm({ ...vehForm, driver_mobile: e.target.value })} /></FormRow>
            <FormRow label="Booth"><input data-testid="form-vbooth" value={vehForm.booth} onChange={(e) => setVehForm({ ...vehForm, booth: e.target.value })} /></FormRow>
            <FormRow label="Capacity"><input data-testid="form-capacity" type="number" value={vehForm.capacity} onChange={(e) => setVehForm({ ...vehForm, capacity: e.target.value })} /></FormRow>
          </div>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShowVeh(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !vehForm.registration_no || !vehForm.driver_name} onClick={saveVehicle}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}
    </>
  );
}
